import React, {
  useRef, useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  TableBody,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableContainer,
  Paper,
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TableSortLabel,
} from '@mui/material';

const adminHeaders = [
  { id: 'timestamp', label: 'Timestamp' },
  { id: 'performedBy', label: 'Performed By' },
  { id: 'action', label: 'Action' },
  { id: 'targetUsername', label: 'Target User' },
  { id: 'previousValue', label: 'Previous Value' },
  { id: 'newValue', label: 'New Value' },
];

const generalHeaders = [
  { id: 'timestamp', label: 'Timestamp' },
  { id: 'performedBy', label: 'Performed By' },
  { id: 'action', label: 'Action' },
  { id: 'targetEntity', label: 'Target Entity' },
  { id: 'metadata', label: 'Metadata' },
  { id: 'ipAddress', label: 'IP Address' },
  { id: 'userAgent', label: 'User Agent' },
  { id: 'previousValue', label: 'Previous Value' },
  { id: 'newValue', label: 'New Value' },
];

function formatTimestamp(timestamp) {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString();
}

function formatValue(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function AuditLogsTable({
  data,
  logType,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
  currentPage,
  pageLimit,
  pageLimitOptions,
  onPageLimitChange,
}) {
  const headers = logType === 'admin' ? adminHeaders : generalHeaders;
  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('timestamp');

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle objects (convert to string for comparison)
      if (typeof aValue === 'object') aValue = JSON.stringify(aValue);
      if (typeof bValue === 'object') bValue = JSON.stringify(bValue);

      // Compare values
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  // Sync horizontal scroll between top scrollbar and table
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const topScroll = topScrollRef.current;

    if (!tableContainer || !topScroll) return undefined;

    const handleTableScroll = () => {
      topScroll.scrollLeft = tableContainer.scrollLeft;
    };

    const handleTopScroll = () => {
      tableContainer.scrollLeft = topScroll.scrollLeft;
    };

    tableContainer.addEventListener('scroll', handleTableScroll);
    topScroll.addEventListener('scroll', handleTopScroll);

    return () => {
      tableContainer.removeEventListener('scroll', handleTableScroll);
      topScroll.removeEventListener('scroll', handleTopScroll);
    };
  }, []);

  const renderAdminRow = (row) => (
    <TableRow key={row.id}>
      <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
      <TableCell>{formatValue(row.performedBy)}</TableCell>
      <TableCell>{formatValue(row.action)}</TableCell>
      <TableCell>{formatValue(row.targetUsername)}</TableCell>
      <TableCell>{formatValue(row.previousValue)}</TableCell>
      <TableCell>{formatValue(row.newValue)}</TableCell>
    </TableRow>
  );

  const renderGeneralRow = (row) => (
    <TableRow key={row.id}>
      <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
      <TableCell>{formatValue(row.performedBy)}</TableCell>
      <TableCell>{formatValue(row.action)}</TableCell>
      <TableCell>{formatValue(row.targetEntity)}</TableCell>
      <TableCell>{formatValue(row.metadata)}</TableCell>
      <TableCell>{formatValue(row.ipAddress)}</TableCell>
      <TableCell>{formatValue(row.userAgent)}</TableCell>
      <TableCell>{formatValue(row.previousValue)}</TableCell>
      <TableCell>{formatValue(row.newValue)}</TableCell>
    </TableRow>
  );

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        sx={{
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '16px',
        }}
        component={Paper}
      >
        <Table sx={{ minWidth: logType === 'admin' ? 900 : 1400 }}>
          <TableHead
            sx={{
              backgroundColor: '#f2f2f26b',
            }}
          >
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.id}
                  sortDirection={orderBy === header.id ? order : false}
                  sx={{
                    color: '#6C6A6A',
                    fontSize: '0.9rem',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === header.id}
                    direction={orderBy === header.id ? order : 'asc'}
                    onClick={() => handleSortRequest(header.id)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row) => (
              logType === 'admin' ? renderAdminRow(row) : renderGeneralRow(row)
            ))}
            {sortedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No audit logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          mt: 2,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Rows per page:
        </Typography>
        <FormControl size="small">
          <Select
            value={pageLimit}
            onChange={(e) => onPageLimitChange(e.target.value)}
            sx={{ minWidth: 70 }}
          >
            {pageLimitOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          Page
          {' '}
          {currentPage}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onPrevPage}
          disabled={!hasPrevPage}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onNextPage}
          disabled={!hasNextPage}
        >
          Next
        </Button>
      </Box>
    </>
  );
}

AuditLogsTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  logType: PropTypes.oneOf(['admin', 'general']),
  hasNextPage: PropTypes.bool,
  hasPrevPage: PropTypes.bool,
  onNextPage: PropTypes.func,
  onPrevPage: PropTypes.func,
  currentPage: PropTypes.number,
  pageLimit: PropTypes.number,
  pageLimitOptions: PropTypes.arrayOf(PropTypes.number),
  onPageLimitChange: PropTypes.func,
};

AuditLogsTable.defaultProps = {
  data: [],
  logType: 'admin',
  hasNextPage: false,
  hasPrevPage: false,
  onNextPage: () => {},
  onPrevPage: () => {},
  currentPage: 1,
  pageLimit: 50,
  pageLimitOptions: [10, 25, 50, 100],
  onPageLimitChange: () => {},
};
